import React, { useState } from 'react';
import {
    Dialog,
    DialogSurface,
    DialogTitle,
    DialogActions,
    Button,
    Textarea, makeStyles,
} from '@fluentui/react-components';
import { FeedbackType, FeedbackRating, FeedbackContext, Feedback } from '../types/feedback';
import * as feedbackService from '../services/feedbackService';
import {useAuth} from "../contexts/AuthContext";

const useStyles = makeStyles({
    primaryButton: {
        backgroundColor: "var(--button-bg, #0f6cbd)",
        color: "var(--button-text, #fff)",
        border: "1px solid var(--button-bg, #0f6cbd)",
        borderRadius: "16px",
        padding: "4px 12px",
        fontSize: "13px",
        minWidth: "0px",
        whiteSpace: "nowrap",
        cursor: "pointer",

        "&:hover": {
            backgroundColor: "var(--button-bg-hover, #0078d4) !important",
            border: "1px solid var(--button-bg-hover, #0078d4) !important",
            color: "#fff !important",
        },

        "&:active": {
            backgroundColor: "var(--button-bg-active, #005ea2) !important",
            border: "1px solid var(--button-bg-active, #005ea2) !important",
            color: "#fff !important",
        },

        "&:focus, &:focus-visible": {
            backgroundColor: "var(--button-bg, #0f6cbd) !important",
            border: "1px solid var(--button-bg, #0f6cbd) !important",
            color: "#fff !important",
            outline: "none",
            boxShadow: "0 0 0 2px var(--button-bg, #0f6cbd)",
        },
    },


    secondaryButton: {
        backgroundColor: "#f0f0f0",
        color: "#000",
        borderRadius: "16px",
        padding: "4px 12px",
        fontSize: "13px",
        minWidth: "0px",
        whiteSpace: "nowrap",
        cursor: "pointer",
        border: "1px solid #ccc",

        "&:hover": {
            backgroundColor: "#e0e0e0",
        },
        "&:active": {
            backgroundColor: "#d0d0d0",
        },
        "&:focus": {
            outline: "none",
            boxShadow: "0 0 0 2px #ccc",
        },
    },
    tagButton: {
        borderRadius: "16px",
        padding: "4px 12px",
        fontSize: "13px",
        minWidth: "0px",
        whiteSpace: "nowrap",
        cursor: "pointer",
        border: "1px solid var(--button-bg,#000)",
        backgroundColor: "transparent",
        color: "var(--button-bg, #000) !important",
        transition: "all 0.2s ease",

        "&:active": {
            backgroundColor: "var(--button-bg, #0f6cbd) !important",
        },
    },

    tagButtonSelected: {
        borderRadius: "16px",
        padding: "4px 12px",
        fontSize: "13px",
        minWidth: "0px",
        whiteSpace: "nowrap",
        cursor: "default",
        border: "1px solid var(--button-bg, #000)",
        backgroundColor: "var(--button-bg, #0f6cbd)",
        color: "var(--button-text, #fff)",
        transition: "all 0.2s ease",

        "&:hover": {
            backgroundColor: "var(--button-bg, #0f6cbd)",
            color: "var(--button-text, #fff)",
        },

        "&:active": {
            backgroundColor: "var(--button-bg, #0f6cbd)",
        },

        "&:focus": {
            backgroundColor: "var(--button-bg, #0f6cbd)",
            outline: "none",
        },
    },
    textarea: {
        width: "100%",
        marginBottom: "10px",
        fontSize: "13px",
        minHeight: "48px",
        maxHeight: "80px",
    },

    infoText: {
        fontSize: "11px",
        color: "#888",
        marginBottom: "10px",
    },

    buttonRow: {
        display: "flex",
        flexWrap: "wrap",
        gap: "8px",
        marginBottom: "12px",
        flexDirection: "row",
    },
});


const feedbackTypeLabels: Record<FeedbackType, string> = {
    BAD_STYLE: 'Stil gefällt mir nicht',
    BAD_STRUCTURE: 'Struktur schlecht',
    BAD_PUNCTUATION: 'Zeichensetzung schlecht',
    BAD_TIMESTAMPS: 'Zeitstempel schlecht',
    INCOMPLETE: 'Anweisungen nicht vollständig gefolgt',
    INCORRECT: 'Antwort inkorrekt',
    MISSING_KEY_INFO: 'Fehlende Schlüsselinformation',
    NO_ANSWER: 'Frage nicht beantwortet',
    ACCURATE: 'Akkurat und detailliert',
    CLEAR: 'Klar und einfach zu verstehen',
    WELL_STRUCTURED: 'Gut strukturiert',
    ENGAGING: 'Spannend und gut angepasst',
    CREATIVE: 'Kreativ und aufschlussreich',
    WRONG_SPEAKER: 'Falscher Sprecher',
    HALLUCINATED: 'Halluziniert',
    OTHER: 'Sonstiges',
};

const feedbackTypeOptionsByContext: Record<FeedbackContext, { positive: FeedbackType[]; negative: FeedbackType[] }> = {
    [FeedbackContext._ChatMessage]: {
        positive: [
            FeedbackType._Accurate,
            FeedbackType._Clear,
            FeedbackType._WellStructured,
            FeedbackType._Engaging,
            FeedbackType._Creative,
        ],
        negative: [
            FeedbackType._BadStyle,
            FeedbackType._Incorrect,
            FeedbackType._Incomplete,
            FeedbackType._NoAnswer,
            FeedbackType._Other,
        ],
    },
    [FeedbackContext._TranscriptText]: { positive: [], negative: [] },
    [FeedbackContext._LlmTaskGeneration]: { positive: [], negative: [] },
};

type FeedbackDialogProps = {
    isOpen: boolean;
    onClose: () => void;
    rating: FeedbackRating;
    context: FeedbackContext;
    chatId: string;
    chatMessageId: string;
    agentId: string;
    initialFeedback?: Feedback;
    onFeedbackChange?: (feedback?: Feedback) => void;
};

export const FeedbackDialog: React.FC<FeedbackDialogProps> = ({
                                                                  isOpen,
                                                                  onClose,
                                                                  rating,
                                                                  context,
                                                                  chatId,
                                                                  chatMessageId,
                                                                  agentId,
                                                                  initialFeedback,
                                                                  onFeedbackChange,
                                                              }) => {
    const [type, setType] = useState<FeedbackType | undefined>(initialFeedback?.type);
    const [message, setMessage] = useState(initialFeedback?.feedbackMessage || '');
    const [saving, setSaving] = useState(false);
    const { getValidAccessToken } = useAuth();
    const styles = useStyles();

    const editing = !!initialFeedback?.id;

    const feedbackTypes =
        (feedbackTypeOptionsByContext[context] &&
            (rating === FeedbackRating._Positive
                ? feedbackTypeOptionsByContext[context].positive
                : feedbackTypeOptionsByContext[context].negative)) ||
        [];

    const allTypes = [...feedbackTypes.filter(ft => ft !== FeedbackType._Other), FeedbackType._Other];

    const dialogTitle =
        rating === FeedbackRating._Positive
            ? 'Was hat dir an der Antwort am besten gefallen?'
            : 'Warum hat dir die Antwort nicht gefallen?';

    const handleSubmit = async () => {
        const accessToken = await getValidAccessToken();
        if (!type) return;
        setSaving(true);
        try {
            const feedback: Feedback = {
                id: initialFeedback?.id,
                chatId,
                chatMessageId,
                rating,
                type,
                feedbackMessage: message,
                context,
                source:"Outlook",
            };
            let result: Feedback;

            if (editing) {
                result = await feedbackService.updateFeedback(feedback, agentId);
            } else {
                result = await feedbackService.createFeedback(feedback, agentId, accessToken);
            }
            onFeedbackChange?.(result);
            onClose();
        } catch (e) {
            console.error(e);
            alert('Error saving feedback');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={isOpen} onOpenChange={(_, d) => !d.open && onClose()}>
            <DialogSurface>
                <DialogTitle style={{ fontSize: 16, marginBottom: 10 }}>{dialogTitle}</DialogTitle>

                <div className={styles.buttonRow}>
                    {allTypes.map((ft) => (
                        <Button
                            key={ft}
                            size="small"
                            className={type === ft ? styles.tagButtonSelected : styles.tagButton}
                            onClick={() => setType(ft)}
                        >
                            {feedbackTypeLabels[ft]}
                        </Button>
                    ))}
                </div>

                <Textarea
                    value={message}
                    onChange={(_, d) => setMessage(d.value)}
                    placeholder="Gib weitere Details an…"
                    className={styles.textarea}
                    rows={2}
                    disabled={saving}
                />

                <div className={styles.infoText}>
                    Mit dem Absenden deines Feedbacks wird der Chatverlauf ebenfalls übermittelt.
                </div>

                <DialogActions style={{ justifyContent: "flex-end", gap: 8 }}>
                    <Button
                        size="small"
                        className={styles.secondaryButton}
                        onClick={onClose}
                        disabled={saving}
                    >
                        Abbrechen
                    </Button>
                    <Button
                        size="small"
                        className={styles.primaryButton}
                        onClick={handleSubmit}
                        disabled={saving || !type}
                    >
                        {saving ? <span style={{ fontSize: 12 }}>Speichern…</span> : editing ? 'Aktualisieren' : 'Absenden'}
                    </Button>
                </DialogActions>
            </DialogSurface>
        </Dialog>
    );
};